import React, { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useWatch, useFormContext } from 'react-hook-form';
import ReactTooltip from 'react-tooltip';
import toFixed from 'accounting-js/lib/toFixed.js';

import { ReactComponent as InfoSvg } from '../../../assets/icons/info.svg';
import { FormComponent, FormComponentProps } from '../../../components/form';
import {
  Tab,
  TabContainer,
  TabContents,
  TabLink,
  TabNavigation,
} from '../../../components/common/tabs/tab-container';
import TabsKeys from '../../../enums/tab-keys';
import { TableColumn } from '../../../components/common/table/table';
import { FormData } from './create';
import Feedingstuff from '../../../interfaces/feedingstuff';
import { DecimalInputComponent } from '../../../components/form/elements/decimal';
import StaticTable from '../../../components/common/table/static-table';
import FieldTypes from '../../../enums/field-types';
import ClassifierPropertyKey from '../../../enums/classifier-property-key';
import css from './summary.module.css';
import FodderNeedPeriodType from '../../../enums/fodder-need-period-type';
import { Option } from '../../../components/form/elements/select';
import ExcelButton from '../../../components/common/table/excel-button/excel-button';
import { useService } from '../../../services/services';
import { useParams } from 'react-router-dom';

export default function Summary(
  name: string,
  periods: string[],
  usedPeriods: string[],
  formData: FormData,
  saveFormData: Function,
  classifiers: Option[],
  onlyTotal: boolean
): FormComponent {
  return ({ ...rest }) => (
    <SummaryComponent
      name={name}
      periods={periods}
      usedPeriods={usedPeriods}
      formValues={formData}
      saveFormData={saveFormData}
      onlyTotal={onlyTotal}
      classifiers={classifiers}
      {...rest}
    />
  );
}

interface FeedingstuffTableRow {
  id: string;
  feedingstuff: string;
  feedingstuff_eat_losses: React.ReactNode | number;
  feedingstuff_storage_losses: React.ReactNode | number;
  amount_in_period_for_animal_group: number;
  natural_mass: number;
  dry_total_t: number;
  feedingstuffValue: Feedingstuff;
  self_produced_t: number;
  dry_distribution_perc: number;
  dry_distribution_perc_formatted: number | React.ReactNode;
  need_to_buy: number;
  costs: number;
}

export function SummaryComponent({
  name,
  periods,
  usedPeriods,
  formValues,
  saveFormData,
  classifiers,
  onlyTotal,
}: FormComponentProps & {
  name: string;
  periods: string[];
  usedPeriods: string[];
  formValues: FormData;
  saveFormData: Function;
  classifiers: Option[];
  onlyTotal: boolean;
}) {
  const { t } = useTranslation();
  const tp = useCallback(
    (key: string) => t('pages.livestock.fodder_need.'.concat(key)),
    [t]
  );
  const { setValue, getValues } = useFormContext();

  const setDefaultFormValues = (
    classifierPropertyKey: ClassifierPropertyKey,
    formItemName: string
  ) => {
    //let stateData = {} as {[key: string]: number};
    periods.forEach((period) => {
      Object.values(formValues.animalGroups[period]).forEach(
        (animalGroup: any) => {
          if (animalGroup.feedingstuffs) {
            Object.values(animalGroup.feedingstuffs).forEach(
              (feedingstuff: any) => {
                //set default form values if not yet set
                const formValuesFeedingstuff = Object.values(
                  classifiers ?? formValues.feedingstuffsClassifiers
                ).find((nut: any) => {
                  return nut.value === feedingstuff.feedingstuff_id;
                }) as any;

                const property =
                  formValuesFeedingstuff &&
                  (Object.values(formValuesFeedingstuff.properties).find(
                    (property: any) =>
                      property.property.key === classifierPropertyKey
                  ) as any);

                if (
                  getValues(
                    name.concat(
                      '.',
                      period,
                      '.',
                      feedingstuff.feedingstuff_id,
                      '.',
                      formItemName
                    )
                  ) === undefined
                ) {
                  const otherPeriod = periods.find((per) => per !== period);
                  const othersPeriodsValue = getValues(
                    name.concat(
                      '.',
                      otherPeriod,
                      '.',
                      feedingstuff.feedingstuff_id,
                      '.',
                      formItemName
                    )
                  );
                  setValue(
                    name.concat(
                      '.',
                      period,
                      '.',
                      feedingstuff.feedingstuff_id,
                      '.',
                      formItemName
                    ),
                    othersPeriodsValue !== undefined
                      ? othersPeriodsValue
                      : property?.value ?? 0
                  );
                }
              }
            );
          }
        }
      );
    });
  };

  setDefaultFormValues(
    ClassifierPropertyKey.FEEDINGSTUFF_EAT_LOSSES_PERC,
    'feedingstuff_eat_losses'
  );
  setDefaultFormValues(
    ClassifierPropertyKey.FEEDINGSTUFF_STORAGE_LOSSES_PERC,
    'feedingstuff_storage_losses'
  );

  const [selectedTab, setSelectedTab] = useState(
    usedPeriods.length === periods.length
      ? TabsKeys.total
      : usedPeriods.some(
          (period: any) => period === FodderNeedPeriodType.WINTER
        )
      ? FodderNeedPeriodType.WINTER
      : FodderNeedPeriodType.SUMMER
  );

  let columns = [
    {
      accessor: 'feedingstuff',
      header: tp('feedingstuffs'),
      className: css['strong'],
    },
    {
      accessor: 'feedingstuff_eat_losses',
      header: tp('feedingstuff_eat_losses'),
      dataType: FieldTypes.DECIMAL,
    },
    {
      accessor: 'feedingstuff_storage_losses',
      header: tp('feedingstuff_storage_losses'),
      dataType: FieldTypes.DECIMAL,
    },
    {
      accessor: 'dry_total_t',
      header: tp('dry_total_t'),
      aggregateAccessor: 'dry_total_t',
      dataType: FieldTypes.DECIMAL,
      roundDecimals: 3,
    },
    {
      accessor: 'dry_distribution_perc_formatted',
      header: (
        <div className={css['header']}>
          <span>{tp('dry_distribution_perc')}</span>
          <InfoSvg
            data-tip
            data-for={`dry_distribution_perc_formatted_header`}
          />
          <ReactTooltip
            id="dry_distribution_perc_formatted_header"
            backgroundColor={'#002A58'}
            place="top"
            effect="solid"
            className={css['tooltip']}
          >
            {tp('dry_distribution_perc_formatted_info')}
          </ReactTooltip>
        </div>
      ),
      aggregateAccessor: 'dry_distribution_perc',
      dataType: FieldTypes.DECIMAL,
    },
    {
      accessor: 'natural_mass',
      header: (
        <div className={css['header']}>
          <span>{tp('natural_mass')}</span>

          <InfoSvg data-tip data-for={`natural_mass_header`} />
          <ReactTooltip
            id="natural_mass_header"
            backgroundColor={'#002A58'}
            place="top"
            effect="solid"
            className={css['tooltip']}
          >
            {tp('natural_mass_info')}
          </ReactTooltip>
        </div>
      ),
      dataType: FieldTypes.DECIMAL,
      className: css['strong'],
      roundDecimals: 3,
    },
    {
      accessor: 'self_produced_t',
      header: tp('self_produced_t'),
      dataType: FieldTypes.DECIMAL,
      className: css['strong'],
      roundDecimals: 3,
    },
    {
      accessor: 'need_to_buy',
      header: tp('need_to_buy'),
      dataType: FieldTypes.DECIMAL,
      className: css['strong'],
      roundDecimals: 3,
    },
    {
      accessor: 'costs',
      header: tp('costs'),
      dataType: FieldTypes.DECIMAL,
      className: css['strong'],
      roundDecimals: 2,
    },
  ] as TableColumn<any>[];

  const formatDryPercentageCell = useCallback(
    (row: any, totalDry: number) => {
      const formValuesFeedingstuff = Object.values(
        classifiers ?? formValues.feedingstuffsClassifiers
      ).find((nut: any) => {
        return nut.value === row.id;
      }) as any;

      const naturalFarmRestrictionProperty =
        formValuesFeedingstuff &&
        (Object.values(formValuesFeedingstuff.properties).find(
          (property: any) =>
            property.property.key ===
            ClassifierPropertyKey.NATURAL_FARMS_DRY_MATTER_RESTRICTION_PERC
        ) as any);

      //formating dry distributin in percentages
      row.dry_distribution_perc =
        totalDry > 0
          ? parseFloat(toFixed((row.dry_total_t * 100) / totalDry, 2))
          : 100;
      if (
        typeof naturalFarmRestrictionProperty.value === 'number' &&
        naturalFarmRestrictionProperty.value < row.dry_distribution_perc
      ) {
        row.dry_distribution_perc_formatted = (
          <div className={css['restriction']}>
            <div>{row.dry_distribution_perc} %</div>
            <div>
              <InfoSvg
                data-tip
                data-for={`natural_farm_restriction-${row.id}`}
              />
              <ReactTooltip
                id={`natural_farm_restriction-${row.id}`}
                backgroundColor={'#eb5757'}
                place="top"
                effect="solid"
                className={css['tooltip']}
              >
                {tp('natural_farm_restriction')}
              </ReactTooltip>
            </div>
          </div>
        );
      } else {
        row.dry_distribution_perc_formatted = row.dry_distribution_perc + ' %';
      }

      return row;
    },
    [tp, formValues.feedingstuffsClassifiers, classifiers]
  );

  const requestData = useCallback(
    (periodsToCount: string[]) => {
      let data = {} as {
        [key: string]: { [key: string]: FeedingstuffTableRow };
      };

      //gather data for table on each period
      periodsToCount.forEach((period) => {
        data[period] = {};
        Object.values(formValues.animalGroups[period]).forEach(
          (animalGroup: any) => {
            if (animalGroup.feedingstuffs) {
              Object.values(animalGroup.feedingstuffs).forEach(
                (feedingstuff: any) => {
                  const formValuesFeedingstuff = Object.values(
                    formValues.feedingstuffs
                  ).find((feed: any) => {
                    return (
                      feed.feedingstuff_id === feedingstuff.feedingstuff_id
                    );
                  }) as any;

                  const feedingstuffsClassifier = (
                    classifiers ?? formValues.feedingstuffsClassifiers
                  ).find(
                    (item: any) => item.value === feedingstuff.feedingstuff_id
                  );

                  if (!(feedingstuff.feedingstuff_id in data[period])) {
                    data[period][feedingstuff.feedingstuff_id] = {
                      amount_in_period_for_animal_group: 0,
                      natural_mass: 0,
                      dry_total_t: 0,
                      need_to_buy: 0,
                      costs: 0,
                    } as FeedingstuffTableRow;
                  }

                  data[period][feedingstuff.feedingstuff_id].id =
                    feedingstuff.feedingstuff_id;
                  data[period][feedingstuff.feedingstuff_id].feedingstuffValue =
                    formValuesFeedingstuff;
                  data[period][feedingstuff.feedingstuff_id].feedingstuff =
                    feedingstuffsClassifier.label +
                    ' (' +
                    (formValuesFeedingstuff.title?.value ??
                      formValuesFeedingstuff.title) +
                    ')';

                  const eatLosseName = name.concat(
                    '.',
                    period,
                    '.',
                    feedingstuff.feedingstuff_id,
                    '.feedingstuff_eat_losses'
                  );
                  data[period][
                    feedingstuff.feedingstuff_id
                  ].feedingstuff_eat_losses = (
                    <FeedingstuffLosses
                      name={eatLosseName}
                      reversePeriodName={name.concat(
                        '.',
                        periods.filter((per) => per !== period)[0],
                        '.',
                        feedingstuff.feedingstuff_id,
                        '.feedingstuff_eat_losses'
                      )}
                      feedingstuffKey={feedingstuff.feedingstuff_id}
                    />
                  );

                  const storageLosseName = name.concat(
                    '.',
                    period,
                    '.',
                    feedingstuff.feedingstuff_id,
                    '.feedingstuff_storage_losses'
                  );
                  data[period][
                    feedingstuff.feedingstuff_id
                  ].feedingstuff_storage_losses = (
                    <FeedingstuffLosses
                      name={storageLosseName}
                      reversePeriodName={name.concat(
                        '.',
                        periods.filter((per) => per !== period)[0],
                        '.',
                        feedingstuff.feedingstuff_id,
                        '.feedingstuff_storage_losses'
                      )}
                      feedingstuffKey={feedingstuff.feedingstuff_id}
                    />
                  );
                  data[period][
                    feedingstuff.feedingstuff_id
                  ].amount_in_period_for_animal_group += parseFloat(
                    feedingstuff.amount_in_period_for_animal_group
                  );
                  data[period][feedingstuff.feedingstuff_id].self_produced_t =
                    formValuesFeedingstuff.planned_to_produce_t ?? 0;
                }
              );
            }
          }
        );
      });

      let newData = {} as { [key: string]: FeedingstuffTableRow };

      //correct gathered values based on selected tab
      Object.entries(data).forEach(
        (
          [period, feedingstuffObjects]: [
            string,
            { [key: string]: FeedingstuffTableRow }
          ],
          index
        ) => {
          Object.entries(feedingstuffObjects).forEach(
            ([feedingstuffRowKey, feedingstuffRow]: [
              string,
              FeedingstuffTableRow
            ]) => {
              if (!(feedingstuffRowKey in newData))
                newData[feedingstuffRowKey] = feedingstuffRow;

              const eatLosseName = name.concat(
                '.',
                period,
                '.',
                feedingstuffRowKey,
                '.feedingstuff_eat_losses'
              );
              const feedingstuff_eat_losses = parseFloat(
                getValues(eatLosseName)
              );
              const storageLosseName = name.concat(
                '.',
                period,
                '.',
                feedingstuffRowKey,
                '.feedingstuff_storage_losses'
              );
              const feedingstuff_storage_losses = parseFloat(
                getValues(storageLosseName)
              );
              const natural_mass =
                feedingstuffRow.amount_in_period_for_animal_group / 1000 +
                ((feedingstuffRow.amount_in_period_for_animal_group / 1000) *
                  feedingstuff_eat_losses) /
                  100 +
                ((feedingstuffRow.amount_in_period_for_animal_group / 1000 +
                  ((feedingstuffRow.amount_in_period_for_animal_group / 1000) *
                    feedingstuff_eat_losses) /
                    100) *
                  feedingstuff_storage_losses) /
                  100;

              newData[feedingstuffRowKey].natural_mass += parseFloat(
                toFixed(natural_mass, 3)
              );
              newData[feedingstuffRowKey].dry_total_t += parseFloat(
                toFixed(
                  (natural_mass * feedingstuffRow.feedingstuffValue.dry) / 100,
                  3
                )
              );

              const need_to_buy =
                natural_mass - feedingstuffRow.self_produced_t;
              newData[feedingstuffRowKey].need_to_buy += parseFloat(
                toFixed(need_to_buy, 3)
              );
              newData[feedingstuffRowKey].costs += parseFloat(
                toFixed(
                  (feedingstuffRow.feedingstuffValue.price_eur_t ?? 0) *
                    need_to_buy,
                  2
                )
              );

              //set corect data total tabs selected
              if (Object.keys(data).length > 1) {
                newData[feedingstuffRowKey].feedingstuff_eat_losses =
                  feedingstuff_eat_losses;
                newData[feedingstuffRowKey].feedingstuff_storage_losses =
                  feedingstuff_storage_losses;
              }
            }
          );
        }
      );

      const totalDry = Object.values(newData).reduce(
        (total, currentItem) => (total = total + currentItem.dry_total_t),
        0
      );

      return {
        rows: Object.values(newData).map((row) => {
          return formatDryPercentageCell(row, totalDry);
        }),
        aggregates: {
          total: {
            dry_total_t: parseFloat(toFixed(totalDry, 3)),
            dry_distribution_perc: '100 %',
          },
        },
      };
    },
    [formValues, name, getValues, periods, formatDryPercentageCell, classifiers]
  );

  const tabNavs = () => {
    let tabs = [];
    if (periods.length === usedPeriods.length) {
      tabs.push(
        <TabLink
          key={`tab-${TabsKeys.total}-summary-nav`}
          tabKey={TabsKeys.total}
          theme={'pages'}
        >
          {tp('tab_'.concat(TabsKeys.total))}
        </TabLink>
      );
    }
    usedPeriods.forEach((period) => {
      tabs.push(
        <TabLink
          key={`tab-${period}-summary-nav`}
          tabKey={period}
          theme={'pages'}
        >
          {tp('tab_'.concat(period))}
        </TabLink>
      );
    });
    return tabs;
  };

  const { fodderNeedService } = useService();
  const { farmId, id } = useParams<{
    farmId: string;
    id: string;
  }>();

  if (onlyTotal) {
    return (
      <>
        <div className={css['export-container']}>
          <ExcelButton
            id={css['fodder-need-export-button']}
            onClick={() =>
              fodderNeedService.exportXls(farmId, id, {}).subscribe()
            }
          />
        </div>
        <StaticTable<any>
          data={requestData([
            FodderNeedPeriodType.WINTER,
            FodderNeedPeriodType.SUMMER,
          ])}
          keyAccessor="id"
          columns={columns}
          aggregateLabels={{ total: tp('total') }}
        />
      </>
    );
  } else {
    const tabContents = () => {
      let tabs = [];
      if (periods.length === usedPeriods.length) {
        tabs.push(
          <Tab key={`tab-${TabsKeys.total}-summary`} tabKey={TabsKeys.total}>
            <StaticTable<any>
              data={requestData([
                FodderNeedPeriodType.WINTER,
                FodderNeedPeriodType.SUMMER,
              ])}
              keyAccessor="id"
              columns={columns}
              aggregateLabels={{ total: tp('total') }}
            />
          </Tab>
        );
      }
      usedPeriods.forEach((period) => {
        tabs.push(
          <Tab key={`tab-${period}-summary`} tabKey={period}>
            <StaticTable<any>
              data={requestData([period])}
              keyAccessor="id"
              columns={columns}
              aggregateLabels={{ total: tp('total') }}
            />
          </Tab>
        );
      });
      return tabs;
    };

    const setTabSelectedProvider = (tab: any, values: any = null) => {
      saveFormData(getValues());
      setSelectedTab(tab);
    };
    return (
      <TabContainer
        selectedTabKey={selectedTab}
        setTab={setTabSelectedProvider}
        tabNavigation={
          <TabNavigation theme={'pages'}>{tabNavs()}</TabNavigation>
        }
        tabContents={<TabContents theme={'pages'}>{tabContents()}</TabContents>}
      />
    );
  }
}

export function FeedingstuffLosses({
  name,
  reversePeriodName,
  feedingstuffKey,
}: {
  name: string;
  reversePeriodName: string;
  feedingstuffKey: string;
}) {
  const { setValue, trigger, getValues } = useFormContext();
  const value = useWatch({ name: name });

  useEffect(() => {
    if (getValues(reversePeriodName) !== undefined)
      setValue(reversePeriodName, parseFloat(value));
    trigger();
  }, [value, feedingstuffKey, reversePeriodName, getValues, setValue, trigger]);

  return <DecimalInputComponent name={name} negative={false} />;
}
